/**
 * Debounced PUT for competition score picks + AppState/unmount flush + stale-response guard.
 */

import {
  useRef,
  useEffect,
  useCallback,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import { AppState } from 'react-native';
import type { AppStateStatus } from 'react-native';
import type { QueryClient } from '@tanstack/react-query';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { isAxiosError } from 'axios';
import { extractErrorMessage } from '../../../helpers/apiClient.helper';
import { logger } from '../../helpers/logger.helper';
import { useDialog } from '../../shared/hooks/useDialog';
import { useSnackbarStore } from '../../shared/stores/snackbarStore';
import { WORLD_CUP_PICK_SAVE_DEBOUNCE_MS } from '../worldcup.constants';
import { worldCupQueryKeys } from '../constants/worldcupQueryKeys';
import { XP_SUMMARY_QUERY_KEY } from '../../economy/hooks/useXpSummary';
import type { UserMatchesOverlayPayload } from '../helpers/worldCupMatchesApi.helper';
import { resolveScoresForPersist } from '../helpers/worldcupPickScoreInput.helper';
import { useWcPickSavedEventStore } from '../stores/wcPickSavedEventStore';
import { WORLD_CUP_PICK_PERSISTENCE_COPY } from './useWorldCupPickPersistence.constants';
import { track as analyticsTrack } from '../../analytics';
import { ANALYTICS_EVENTS } from '../../analytics';

export type WorldCupPredictionsState = Record<string, { home: string; away: string }>;

type ApiClientFn = <T = unknown>(
  endpoint: string,
  options?: AxiosRequestConfig,
) => Promise<AxiosResponse<T>>;

interface PickSaveResponseBody {
  success?: boolean;
  data?: {
    predictedHomeScore?: number;
    predictedAwayScore?: number;
  };
  error?: string;
}

export interface UseWorldCupPickPersistenceArgs {
  competitionId: string;
  persistenceEnabled: boolean;
  predictions: WorldCupPredictionsState;
  setPredictions: Dispatch<SetStateAction<WorldCupPredictionsState>>;
  queryClient: QueryClient;
  apiClientRef: MutableRefObject<ApiClientFn>;
  getCanPersistMatch: (matchId: string) => boolean;
}

export interface UseWorldCupPickPersistenceResult {
  onPredictionChange: (matchId: string, home: string, away: string) => void;
}

function shouldFlushAppState(state: AppStateStatus): boolean {
  return state === 'background' || state === 'inactive';
}

export function useWorldCupPickPersistence({
  competitionId,
  persistenceEnabled,
  predictions,
  setPredictions,
  queryClient,
  apiClientRef,
  getCanPersistMatch,
}: UseWorldCupPickPersistenceArgs): UseWorldCupPickPersistenceResult {
  const { showAlert } = useDialog();
  const predictionsRef = useRef(predictions);
  const generationByMatchRef = useRef<Record<string, number>>({});
  const debounceTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const executeSaveRef = useRef<(matchId: string) => void>(() => {});
  const flushDebouncedRef = useRef<() => void>(() => {});

  useEffect(() => {
    predictionsRef.current = predictions;
  }, [predictions]);

  const mergePickIntoMatchesCache = useCallback(
    (matchId: string, home: number, away: number) => {
      queryClient.setQueryData<UserMatchesOverlayPayload>(
        worldCupQueryKeys.matchesMe(competitionId),
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            picks: {
              ...prev.picks,
              [matchId]: {
                home,
                away,
                isMonkeyPick: false,
                pointsEarned: prev.picks[matchId]?.pointsEarned ?? null,
              },
            },
          };
        },
      );
    },
    [competitionId, queryClient],
  );

  useEffect(() => {
    executeSaveRef.current = (matchId: string) => {
      if (!persistenceEnabled || !getCanPersistMatch(matchId)) return;

      const pred = predictionsRef.current[matchId];
      if (!pred) return;

      const resolved = resolveScoresForPersist(pred.home, pred.away);
      if (!resolved) return;
      const predictedHome = resolved.home;
      const predictedAway = resolved.away;

      const sentGeneration = generationByMatchRef.current[matchId] ?? 0;

      void (async () => {
        try {
          const res = await apiClientRef.current<PickSaveResponseBody>(
            `/competitions/${competitionId}/picks/${matchId}`,
            {
              method: 'PUT',
              data: { predictedHomeScore: predictedHome, predictedAwayScore: predictedAway },
            },
          );

          const body = res.data;
          if (!body?.success || !body.data) {
            return;
          }

          const ph = body.data.predictedHomeScore;
          const pa = body.data.predictedAwayScore;
          if (typeof ph !== 'number' || typeof pa !== 'number') {
            return;
          }

          if ((generationByMatchRef.current[matchId] ?? 0) !== sentGeneration) {
            return;
          }

          const latest = predictionsRef.current[matchId];
          const latestResolved = latest
            ? resolveScoresForPersist(latest.home, latest.away)
            : null;
          if (!latestResolved || latestResolved.home !== ph || latestResolved.away !== pa) {
            return;
          }

          mergePickIntoMatchesCache(matchId, ph, pa);
          // A saved pick can earn XP (first-pick bonus) server-side in-request, so refetch
          // the summary now — the header ring/LV and the "+X XP" flash update on this screen
          // instead of waiting for the next focus refetch (specs/xp-live-feedback AC-1).
          void queryClient.invalidateQueries({ queryKey: XP_SUMMARY_QUERY_KEY });
          analyticsTrack(ANALYTICS_EVENTS.BET_PLACED, { match_id: matchId });
          // Reflect a resolved blank side ("2 vs -" saved as "2 vs 0") back into the
          // local input so the UI matches what was persisted. No-op when already equal.
          setPredictions((prev) => {
            const cur = prev[matchId];
            if (!cur) return prev;
            const nextHome = String(ph);
            const nextAway = String(pa);
            if (cur.home === nextHome && cur.away === nextAway) return prev;
            return { ...prev, [matchId]: { home: nextHome, away: nextAway } };
          });
          // On a brand-new user's first pick the WC→Arena onboarding snackbar
          // fires; suppress the generic "Pick saved" toast so the two don't
          // replace each other mid-animation (read savedCount BEFORE bump).
          const pickSavedStore = useWcPickSavedEventStore.getState();
          const suppressToast =
            pickSavedStore.bridgePending && pickSavedStore.savedCount === 0;
          pickSavedStore.bump();
          if (!suppressToast) {
            useSnackbarStore.getState().showSnackbar(
              WORLD_CUP_PICK_PERSISTENCE_COPY.PICK_SAVED_MESSAGE,
              'success',
            );
          }
        } catch (err: unknown) {
          if (isAxiosError(err) && err.response?.status === 409) {
            void showAlert({
              title: WORLD_CUP_PICK_PERSISTENCE_COPY.PICK_LOCKED_TITLE,
              message: WORLD_CUP_PICK_PERSISTENCE_COPY.PICK_LOCKED_MESSAGE,
              type: 'default',
            });
            void queryClient.invalidateQueries({
              queryKey: worldCupQueryKeys.matchesStatic(competitionId),
            });
            void queryClient.invalidateQueries({
              queryKey: worldCupQueryKeys.matchesMe(competitionId),
            });
            return;
          }

          // Match not found — static cache is stale (e.g. post re-seed). Invalidate
          // so the client gets fresh match IDs; the user's local pick stays visible.
          if (isAxiosError(err) && err.response?.status === 404) {
            void queryClient.invalidateQueries({
              queryKey: worldCupQueryKeys.matchesStatic(competitionId),
            });
            return;
          }

          // Anonymous guests are hard-blocked server-side. Trigger the themed
          // "Don't lose your rank" prompt via PostFirstPickPromptManager instead
          // of logging a scary warning. The local pick input stays visible so
          // the prompt has context; nothing is persisted server-side.
          if (
            isAxiosError(err)
            && err.response?.status === 403
            && (err.response.data as { code?: string } | undefined)?.code === 'ANONYMOUS_USER'
          ) {
            useWcPickSavedEventStore.getState().bump();
            return;
          }

          // Canceled requests (navigation/abort) are expected — never surface them.
          if (isAxiosError(err) && err.code === 'ERR_CANCELED') {
            return;
          }

          // Genuine failure (network down after retries, 5xx). Tell the user their
          // pick did not save so they don't believe a silent failure succeeded.
          logger.warn('[useWorldCupPickPersistence] Pick save failed', {
            matchId,
            message: extractErrorMessage(err, 'save failed'),
          });
          useSnackbarStore.getState().showSnackbar(
            WORLD_CUP_PICK_PERSISTENCE_COPY.PICK_SAVE_FAILED_MESSAGE,
            'error',
          );
        }
      })();
    };
  }, [
    apiClientRef,
    competitionId,
    getCanPersistMatch,
    mergePickIntoMatchesCache,
    persistenceEnabled,
    queryClient,
    setPredictions,
    showAlert,
  ]);

  useEffect(() => {
    flushDebouncedRef.current = () => {
      const timers = debounceTimersRef.current;
      const ids = Object.keys(timers);
      for (const id of ids) {
        clearTimeout(timers[id]);
        delete timers[id];
        executeSaveRef.current(id);
      }
    };
  }, []);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (next: AppStateStatus) => {
      if (shouldFlushAppState(next)) {
        flushDebouncedRef.current();
      }
    });
    return () => {
      sub.remove();
      flushDebouncedRef.current();
    };
  }, []);

  const scheduleDebouncedSave = useCallback((matchId: string) => {
    const timers = debounceTimersRef.current;
    if (timers[matchId]) {
      clearTimeout(timers[matchId]);
    }
    timers[matchId] = setTimeout(() => {
      delete debounceTimersRef.current[matchId];
      executeSaveRef.current(matchId);
    }, WORLD_CUP_PICK_SAVE_DEBOUNCE_MS);
  }, []);

  const onPredictionChange = useCallback(
    (matchId: string, home: string, away: string) => {
      setPredictions((prev) => ({ ...prev, [matchId]: { home, away } }));
      if (!persistenceEnabled) return;
      generationByMatchRef.current[matchId] = (generationByMatchRef.current[matchId] ?? 0) + 1;
      scheduleDebouncedSave(matchId);
    },
    [persistenceEnabled, scheduleDebouncedSave, setPredictions],
  );

  return { onPredictionChange };
}
